import { Link, useLocation } from "react-router-dom";
import {
  FaInstagram,
  FaGraduationCap,
  FaHeart,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaEnvelope,
  FaExternalLinkAlt,
} from "react-icons/fa";
import "./Footer.css";

const schoolDirectoryLinks = [
  { to: "/schools", label: "All Andhra Pradesh Schools" },
  { to: "/schools/ssc", label: "SSC Schools" },
  { to: "/schools/cbse", label: "CBSE Schools" },
  { to: "/schools/cisce", label: "CISCE Schools" },
  { to: "/schools/top-schools/all-boards", label: "Top Schools (All Boards)" },
  { to: "/schools/top-schools/ssc", label: "Top Schools SSC" },
  { to: "/schools/top-schools/cbse", label: "Top Schools CBSE" },
  { to: "/schools/top-schools/cisce", label: "Top Schools CISCE" },
  { to: "/guides/what-is-ssc-board", label: "What is SSC Board?" },
  { to: "/guides/what-is-cbse-board", label: "What is CBSE Board?" },
  { to: "/guides/what-is-cisce-board", label: "What is CISCE Board?" },
  // District-specific links will be populated once AP district data is added.
];

const collegeLinks = [
  { to: "/intermediate/colleges", label: "Intermediate Colleges" },
  { to: "/intermediate/colleges/andhra-pradesh", label: "Intermediate District Pages" },
  // District-specific girls-college links will be populated once AP district data is added.
  { to: "/top-10/mpc-colleges", label: "Top MPC Colleges" },
  { to: "/top-10/bipc-colleges", label: "Top BiPC Colleges" },
  { to: "/colleges/degree", label: "Degree Colleges" },
  { to: "/colleges/btech", label: "B.Tech Colleges" },
  { to: "/colleges/btech/district-wise", label: "B.Tech District-wise" },
  { to: "/top-10/engineering-colleges", label: "Top B.Tech Colleges" },
  { to: "/why-btech", label: "Why B.Tech Guide" },
  { to: "/why-cse", label: "Why CSE Guide" },
  { to: "/why-aiml", label: "Why AI/ML Guide" },
  { to: "/colleges/pharmacy", label: "Pharmacy Colleges" },
  { to: "/top-10", label: "Top 10 Rankings" },
];

const resourcesLinks = [
  { to: "/guides", label: "Admission Guides Hub" },
  { to: "/faq", label: "FAQ Hub" },
  { to: "/eamcet", label: "EAMCET Guide" },
  { to: "/results", label: "Latest Results" },
  { to: "/eamcet/important-questions", label: "Important Questions" },
];

const companyLinks = [
  { to: "/about-us", label: "About Us" },
  { to: "/advertise-with-us", label: "Advertise" },
  { to: "/help", label: "Help" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-conditions", label: "Terms and Conditions" },
  { to: "/disclaimer", label: "Disclaimer" },
  { to: "/contact-us", label: "Contact / Report" },
];

const topSchoolDistricts = [];

const topIntermediateDistricts = [];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-top-cta">
          <div>
            <h3>Explore Andhra Pradesh's Most Complete Education Directory</h3>
            <p>Schools, colleges, district-wise listings, and admission resources in one place.</p>
          </div>
          <div className="footer-top-actions">
            <Link to="/schools" className="footer-cta-btn primary">Browse Schools</Link>
            <Link to="/intermediate/colleges" className="footer-cta-btn">Browse Colleges</Link>
          </div>
        </div>

        <div className="footer-main-grid">
          <div className="footer-brand">
            <div className="brand-logo">
              <FaGraduationCap className="brand-icon" />
              <div className="brand-text">
                <h3>Andhra Pradesh Education Hub</h3>
                <span className="brand-sub">School, Intermediate and Degree Guidance</span>
              </div>
            </div>
            <p className="brand-desc">
              Built for Andhra Pradesh students and parents to discover verified school and college information in one
              place. Explore board-wise school listings, district-wise pages, intermediate stream guidance, and
              degree-college discovery with practical admission support.
            </p>
            <ul className="brand-points">
              <li>School directory first: SSC, CBSE, CISCE with district filters</li>
              <li>Intermediate and stream-wise guidance for MPC, BiPC, CEC, MEC, HEC</li>
              <li>Degree-college exploration with useful comparison links</li>
            </ul>
            <div className="social-links">
              <a
                href="https://www.instagram.com/apeducationhubofficial/"
                className="social-icon"
                aria-label="Instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram />
              </a>
              <Link to="/contact-us" className="social-icon" aria-label="Contact Us">
                <FaEnvelope />
              </Link>
            </div>
          </div>

          <div className="footer-link-panels">
            <section className="footer-panel">
              <h4>School Directory</h4>
              <div className="footer-links">
                {schoolDirectoryLinks.map((item) => (
                  <Link key={item.to} to={item.to} className="footer-link">{item.label}</Link>
                ))}
              </div>
            </section>

            <section className="footer-panel">
              <h4>Colleges & Courses</h4>
              <div className="footer-links">
                {collegeLinks.map((item) => (
                  <Link key={item.to} to={item.to} className="footer-link">{item.label}</Link>
                ))}
              </div>
            </section>

            <section className="footer-panel">
              <h4>Resources</h4>
              <div className="footer-links">
                {resourcesLinks.map((item) => (
                  <Link key={item.to} to={item.to} className="footer-link">{item.label}</Link>
                ))}
              </div>
            </section>

            <section className="footer-panel">
              <h4>Company</h4>
              <div className="footer-links">
                {companyLinks.map((item) => (
                  <Link key={item.to} to={item.to} className="footer-link">{item.label}</Link>
                ))}
              </div>
            </section>
          </div>
        </div>

        <div className="footer-district-strip">
          <h5>Popular School District Pages</h5>
          <div className="footer-district-links">
            {topSchoolDistricts.map((d) => (
              <Link key={d.slug} to={`/schools/district/${d.slug}`} className="footer-district-pill">
                {d.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="footer-district-strip">
          <h5>Popular Intermediate District Pages</h5>
          <div className="footer-district-links">
            {topIntermediateDistricts.map((d) => (
              <Link
                key={d.slug}
                to={`/intermediate/colleges/andhra-pradesh/${d.slug}`}
                className="footer-district-pill"
              >
                {d.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="footer-bottom">
          <div className="copyright">
            <p>&copy; {currentYear} Andhra Pradesh Education Hub. All rights reserved.</p>
            <p className="made-for ">
              Design and Developed by <a href="https://peracause.com" target="_blank" rel="noopener noreferrer">PER A CAUSE</a>
              {/* <FaGraduationCap className="brand-icon" /> <FaHeart style={{ color: "#fb7185" }} /> */}
            </p>
          </div>
          <div className="bottom-links">
            <span><FaMapMarkerAlt /> Andhra Pradesh</span>
            {!isHomePage && (
              <a
                href="https://wa.me/916302388294?text=Hi%20Andhra%20Pradesh%20Education%20Hub%2C%20I%20need%20guidance."
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaWhatsapp /> Connect on WhatsApp
              </a>
            )}
            <a href="mailto:contact@apeducationhub.com">
              <FaEnvelope /> contact@apeducationhub.com
            </a>
            {/* <a
              href="https://www.instagram.com/apeducationhubofficial/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Instagram <FaExternalLinkAlt style={{ fontSize: "0.7rem" }} />
            </a> */}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
